import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useActiveAccount } from "applesauce-react/hooks";

import { useNostrReplaceable } from "./nostr";
import useEventUpdate from "./use-event-update";
import { NWC_KIND, NWC_D_TAG, NwcCredentials, decryptNwcCredentials } from "../services/nwc-credentials";
import { NwcConnection, parseNwcUri, nwcGetBalance, nwcPayInvoice, nwcMakeInvoice } from "../services/nwc";
import { queryKeys } from "../helpers/query-keys";
import { nip44 } from "nostr-tools";

interface NwcWalletState {
  connection: NwcConnection | null;
  balance: number | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  payInvoice: (bolt11: string) => Promise<{ preimage: string }>;
  makeInvoice: (amount: number, desc?: string) => Promise<{ invoice: string; payment_hash: string }>;
}

/** Poll for the signer key becoming available after password unlock */
function useSignerKey(account: any): Uint8Array | undefined {
  const [key, setKey] = useState<Uint8Array | undefined>(() => account?.signer?.key ?? undefined);

  useEffect(() => {
    const k = account?.signer?.key;
    if (k) { setKey(k); return; }
    // Poll for unlock (PasswordAccount.signer.key becomes non-null after unlock)
    const interval = setInterval(() => {
      const k2 = account?.signer?.key;
      if (k2) { setKey(k2); clearInterval(interval); }
    }, 500);
    return () => clearInterval(interval);
  }, [account]);

  return key;
}

export default function useNwcWallet(): NwcWalletState {
  const account = useActiveAccount();
  const pubkey = account?.pubkey;
  const signerKey = useSignerKey(account);

  // TanStack Query handles: initial fetch, retry, dedup for the credential event
  const { event: credentialEvent } = useNostrReplaceable(
    pubkey ? { kind: NWC_KIND, pubkey, identifier: NWC_D_TAG } : undefined,
  );
  useEventUpdate(credentialEvent?.id);

  const [connection, setConnection] = useState<NwcConnection | null>(null);
  const [decryptError, setDecryptError] = useState<string | null>(null);

  // Decrypt NWC URI when event loads and account is unlocked
  useEffect(() => {
    if (!credentialEvent || !signerKey || !pubkey) {
      setConnection(null);
      return;
    }
    if (!credentialEvent.content) {
      setConnection(null);
      return;
    }
    try {
      const conversationKey = nip44.v2.utils.getConversationKey(signerKey, pubkey);
      const creds: NwcCredentials = decryptNwcCredentials(credentialEvent.content, conversationKey);
      const conn = parseNwcUri(creds.uri);
      setConnection(conn);
      setDecryptError(null);
    } catch (e) {
      console.warn("Failed to decrypt NWC credentials:", e);
      setDecryptError("Failed to decrypt NWC credentials");
      setConnection(null);
    }
  }, [credentialEvent, signerKey, pubkey]);

  // TanStack Query manages the balance polling
  const {
    data: balance,
    isLoading,
    error: balanceError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.nwc.balance(connection?.walletPubkey ?? ""),
    queryFn: () => nwcGetBalance(connection!),
    enabled: !!connection,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const refresh = useCallback(() => { refetch(); }, [refetch]);

  const payInvoice = useCallback(
    async (bolt11: string) => {
      if (!connection) throw new Error("No NWC connection");
      return nwcPayInvoice(connection, bolt11);
    },
    [connection],
  );

  const makeInvoice = useCallback(
    async (amount: number, desc?: string) => {
      if (!connection) throw new Error("No NWC connection");
      return nwcMakeInvoice(connection, amount, desc);
    },
    [connection],
  );

  return {
    connection,
    balance: balance ?? null,
    isLoading,
    error: decryptError || (balanceError ? "Failed to fetch balance" : null),
    refresh,
    payInvoice,
    makeInvoice,
  };
}
